import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import OptionSelect from './OptionSelect';

export default function MemberMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ members, vendors, isReady }, setState] = useState({ members: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: members } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getMemberMappings();
      const { data: vendors } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getVendors();
      if (!isMounted.current) return;
      setState({ members, vendors, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = ({ memberId, sageVendorId }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      members: state.members.map((m) => (m.id === memberId ? { ...m, sageVendorId } : m)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Member Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = members.map((m) => ({ memberId: m.id, sageVendorId: m.sageVendorId }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateMemberMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Member</Table.Column>
                <Table.Column>Sage Vendor</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {members.map((member) => (
                  <Table.BoxRow key={member.id}>
                    <Table.Cell>
                      {member.name}
                      {member.email && <small style={{ marginLeft: '0.5rem' }}>{member.email}</small>}
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={vendors}
                        typeLabel="Sage Vendor"
                        value={member.sageVendorId}
                        onChange={(event) =>
                          updateMapping({ memberId: member.id, sageVendorId: event.target.value || null })
                        }
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
